import React, { useEffect } from 'react';
import { toast } from 'sonner';
import { Placeholder, Card } from 'react-bootstrap';
import { format } from 'date-fns';

// 1. Impor Highcharts dari modul highstock
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';

import useNumberOfRoomBooked from '../../../../services/cm-room-report/hooks/useNumberOfRoomBooked';

interface NumberOfRoomBookedHighchartsProps {
    propertyId: string | undefined;
    startDate: Date;
    endDate: Date;
}

const NumberOfRoomBookedHighcharts: React.FC<NumberOfRoomBookedHighchartsProps> = ({ propertyId, startDate, endDate }) => {
    const { data: numberOfRoomBookedData, isFetching } = useNumberOfRoomBooked({
        filter: {
            property_id: propertyId || "",
            start_date: format(startDate, 'yyyy-MM-dd'),
            end_date: format(endDate, 'yyyy-MM-dd')
        }
    });

    // Toast notifikasi saat data berhasil di-load
    useEffect(() => {
        if (!isFetching && numberOfRoomBookedData) {
            toast.success('Room booked data loaded successfully.');
        }
    }, [numberOfRoomBookedData, isFetching]);

    // 2. Format data API ke format [timestamp, value] yang optimal untuk Highstock
    const chartData = numberOfRoomBookedData?.data.map(item => [
        new Date(item.date).getTime(),
        item.total_booked_rooms
    ]) || [];

    // 3. Konfigurasi opsi untuk Highstock Chart
    const getOptions = (): Highcharts.Options => ({
        chart: {
            type: 'area',
            backgroundColor: 'transparent'
        },
        title: {
            text: 'Number of Rooms Booked Over Time'
        },
        xAxis: {
            type: 'datetime'
        },
        yAxis: {
            title: {
                text: 'Total Booked Rooms'
            },
            min: 0 // Memastikan sumbu Y dimulai dari 0
        },
        tooltip: {
            pointFormat: '<b>{point.y}</b> rooms booked on {point.x:%A, %b %e, %Y}',
            shared: true
        },
        legend: {
            enabled: true
        },
        plotOptions: {
            area: {
                fillColor: {
                    linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                    stops: [
                        [0, (Highcharts.getOptions().colors?.[0] || '#4682B4') as string],
                        [1, Highcharts.color(Highcharts.getOptions().colors?.[0] || '#4682B4').setOpacity(0).get('rgba') as string]
                    ]
                },
                marker: {
                    radius: 2
                },
                lineWidth: 1,
                states: {
                    hover: {
                        lineWidth: 1
                    }
                },
                threshold: null
            }
        },
        series: [{
            type: 'area',
            name: 'Booked Rooms',
            data: chartData,
            color: '#4682B4'
        }],
        // 4. Aktifkan navigator sebagai pengganti <Brush>
        navigator: {
            enabled: true,
            adaptToUpdatedData: false,
            series: {
                data: chartData
            }
        },
        scrollbar: {
            enabled: true
        },
        // Nonaktifkan tombol range (1m, 3m, dst.) karena range sudah ditentukan
        rangeSelector: {
            enabled: false
        },
        credits: {
            enabled: false
        }
    });

    return (
        <Card className="h-100 shadow">
            <Card.Body>
                {isFetching ? (
                    <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Placeholder as="div" animation="wave" style={{ width: '100%' }}>
                            <Placeholder xs={12} style={{ height: 400, backgroundColor: '#e0e0e0' }} />
                        </Placeholder>
                    </div>
                ) : (
                    <HighchartsReact
                        highcharts={Highcharts}
                        // 5. Gunakan 'stockChart' sebagai constructorType
                        constructorType={'stockChart'}
                        options={getOptions()}
                        containerProps={{ style: { height: '400px', width: '100%' } }}
                    />
                )}
            </Card.Body>
        </Card>
    );
}

export default NumberOfRoomBookedHighcharts;